import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { apiCall } from 'app/shared/lib/essette-api';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { Toaster } from 'app/shared/lib/toaster';
import { localSort } from 'app/shared/lib/sorting';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { SaveDocumentCategory } from 'app/core/components/documents/save-document-category';
import * as Core from 'app/core/models/essette-api';
import { DocumentsService } from 'app/core/services/documents-service';
import { IDocumentsConfig } from 'app/core/models/essette-interfaces';

interface CustomDocumentCategory extends Core.DocumentCategory {
    // Flag Value For Grid Column Sorting
    flagValue?: string | null;
    // Flag Display For Grid Column Display
    flagDisplay?: string | null;
    // Flag Tooltip status list For Grid Column
    flagToolTipDesc?: string | null;
}

@autoinject()
export class DocumentCategoriesGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public documentsConfig?: IDocumentsConfig;

    protected documentCategories: ReadonlyArray<CustomDocumentCategory> | null = null;
    protected documentFlags: ReadonlyArray<Core.DocumentFlag> = [];

    protected documentCategory: Core.DocumentCategory | null = null;

    private apiSorting: Sorting = new Sorting();
    private localSorting: Sorting = new Sorting();
    private localSortProperties: string[] = ['flagValue'];
    protected resultCount: number | null = 0;
    protected pageSize: number = 25;
    protected includeInactive: boolean = false;

    protected validate = validate;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    protected $saveDocumentCategoryModal!: IPopup;
    protected $form!: IForm;
    protected $documentCategoryForm!: Element;
    protected $saveDocumentCategory!: SaveDocumentCategory;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: DocumentsService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(!this.documentsConfig) { throw new Error('Bindable property [documents-config] was not set'); }

        this.service.configure(this.documentsConfig);

        this.reload();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { localSorting, apiSorting } = this;

        //If LocalSort
        if(this.localSortProperties.indexOf(property) > -1) {
            const documentCategories: CustomDocumentCategory[] = <CustomDocumentCategory[]>this.documentCategories;
            if(direction === null) {
                localSorting.remove(property);
                // if no local sorts, revert to API sorts by reloading
                if(localSorting.entries.length === 0) this.reload();
            } else {
                localSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            localSort(documentCategories, localSorting);
        }
        // Else If API Sort
        else {
            if(direction === null) {
                apiSorting.remove(property);
            } else {
                apiSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            this.reload();
        }
    }

    protected formSuccess({ detail }: FormSuccessCustomEvent) {
        this.$saveDocumentCategoryModal.close();

        if(detail.form.reset) detail.form.reset();

        // reload grid
        this.reload();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await Promise.all([
            this.loadDocumentCategoryFlags(),
            this.loadDocumentCategories()
        ]);
    }

    protected saveDocumentCategory(documentCategory: Core.DocumentCategory): void {
        this.$saveDocumentCategory.documentCategory = documentCategory;
        this.documentCategory = documentCategory;

        this.$saveDocumentCategoryModal.open();
    }

    protected saveDocumentCategoryClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$saveDocumentCategoryModal.close();
    }

    // Set Flag Display
    protected setDocumentFlags = (documentCategories: CustomDocumentCategory[]): CustomDocumentCategory[] => {
        documentCategories.forEach(category => {
            if(category?.flags && category.flags.length > 0) {
                const flagCount = category.flags.length;

                // If All Statuses
                if(flagCount >= this.documentFlags.length) {
                    category.flagDisplay = category.flagValue = "All Flags";
                }
                // If One or More Statuses
                else {
                    if(flagCount == 1) {
                        category.flagValue = category.flags[0].description;
                        category.flagDisplay = category.flags[0].$display;
                    }
                    else {
                        category.flagDisplay = category.flagValue = flagCount.toString() + ' Flags';
                        const flagList = category.flags.flatMap(f => f.description ?? "")
                            .sort((a, b) => a.localeCompare(b));

                        let listDisplay = '<ul>';
                        flagList.forEach(f => {
                            listDisplay += '<li>' + f + '</li>';
                        })
                        listDisplay += '</ul>'

                        category.flagToolTipDesc = listDisplay;
                    }
                }
            }
            else {
                category.flagDisplay = category.flagValue = "None";
            }
        });

        return documentCategories;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async activateDocumentCategory(documentCategory: Core.DocumentCategory): Promise<void> {
        // Show confirmation dialog only if deactivating
        if(documentCategory.isActive) {
            const result = await confirmation.showDeactivate('core:document.category.attached.$long', 1);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }

            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateDocumentCategory(documentCategory.id!, !documentCategory.isActive);
            isSuccessful = response.isSuccessful;
            this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('core:document.category.attached.$long', !documentCategory.isActive, isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async loadDocumentCategoryFlags(): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.service.loadDocumentFlags({ all: true });
            this.documentFlags = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadDocumentCategories(): Promise<void> {
        const includes: string[] = ['flags'];

        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.apiSorting,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadCategories(options, includes);
            this.documentCategories = this.setDocumentFlags(<CustomDocumentCategory[]>response.results);
            this.resultCount = response.count;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
